import { useState, useEffect, useCallback, useRef } from 'react'

const NAVY = '#0b2347'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

export default function AdminTrustPanel({ token }) {
  const [activeSubTab, setActiveSubTab] = useState('testimonials')
  const [testimonials, setTestimonials] = useState([])
  const [caseStudies, setCaseStudies] = useState([])
  const [textTestimonials, setTextTestimonials] = useState([])
  const [loading, setLoading] = useState(true)

  // Modal States
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('testimonial') // 'testimonial' | 'casestudy'
  const [itemToDelete, setItemToDelete] = useState(null) // { type, id }
  
  // Testimonial Form
  const [clientName, setClientName] = useState('')
  const [location, setLocation] = useState('')
  const [link, setLink] = useState('')
  const [quote, setQuote] = useState('')
  const [rating, setRating] = useState('5')
  
  // Case Study Form
  const [title, setTitle] = useState('')
  const [challenge, setChallenge] = useState('')
  const [strategy, setStrategy] = useState('')
  const [result, setResult] = useState('')
  const [coverImage, setCoverImage] = useState(null)
  const fileInputRef = useRef(null)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/testimonials')
      if(res.ok) setTestimonials(await res.json())
    } catch {}
  }, [])

  const fetchTextTestimonials = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/texttestimonials')
      if(res.ok) setTextTestimonials(await res.json())
    } catch {}
  }, [])

  const fetchCaseStudies = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/casestudies')
      if(res.ok) setCaseStudies(await res.json())
    } catch {}
  }, [])

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchTestimonials(), fetchCaseStudies(), fetchTextTestimonials()]).finally(() => setLoading(false))
  }, [fetchTestimonials, fetchCaseStudies, fetchTextTestimonials])

  const handleOpenModal = (type) => {
    setModalType(type)
    setClientName(''); setLocation(''); setLink(''); setQuote(''); setRating('5');
    setTitle(''); setChallenge(''); setStrategy(''); setResult(''); setCoverImage(null);
    setShowModal(true)
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (event) => setCoverImage(event.target.result)
    reader.readAsDataURL(file)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (modalType === 'texttestimonial') {
        if (!clientName || !quote || !coverImage) { alert("Author Name, Quote, and Image are required."); setIsSubmitting(false); return; }
        await fetch('/api/admin/texttestimonials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
          body: JSON.stringify({ author: clientName, location, text: quote, image: coverImage })
        })
        fetchTextTestimonials()
      } else if (modalType === 'testimonial') {
        if (!clientName || !link) { alert("Client Name and YouTube Link are required."); setIsSubmitting(false); return; }
        await fetch('/api/admin/testimonials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
          body: JSON.stringify({ clientName, location, link, quote, rating })
        })
        fetchTestimonials()
      } else {
        if (!title || !coverImage) { alert("Title and Cover Image are required."); setIsSubmitting(false); return; }
        await fetch('/api/admin/casestudies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
          body: JSON.stringify({ title, challenge, strategy, result, coverImage })
        })
        fetchCaseStudies()
      }
      setShowModal(false)
    } catch (err) {
      alert("Failed to save.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const requestDelete = (type, id) => {
    setItemToDelete({ type, id })
  }

  const confirmDelete = async () => {
    if (!itemToDelete) return
    const { type, id } = itemToDelete
    await fetch(`/api/admin/${type}/${id}`, { method: 'DELETE', headers: { 'x-admin-token': token } })
    if(type === 'testimonials') fetchTestimonials()
    else if(type === 'texttestimonials') fetchTextTestimonials()
    else fetchCaseStudies()
    setItemToDelete(null)
  }

  const btnStyle = { padding: '10px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontFamily: FONT, fontWeight: 600, transition: 'all 0.2s', fontSize: '13px' }
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontFamily: FONT, fontSize: '13px', marginTop: '6px' }
  const labelStyle = { fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#475569' }

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', background: '#e2e8f0', padding: '4px', borderRadius: '10px', width: 'fit-content' }}>
        <button onClick={() => setActiveSubTab('texttestimonials')} style={{ ...btnStyle, background: activeSubTab === 'texttestimonials' ? '#fff' : 'transparent', color: activeSubTab === 'texttestimonials' ? NAVY : '#64748b', boxShadow: activeSubTab === 'texttestimonials' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>Text Testimonials</button>
        <button onClick={() => setActiveSubTab('testimonials')} style={{ ...btnStyle, background: activeSubTab === 'testimonials' ? '#fff' : 'transparent', color: activeSubTab === 'testimonials' ? NAVY : '#64748b', boxShadow: activeSubTab === 'testimonials' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>Video Testimonials</button>
        <button onClick={() => setActiveSubTab('casestudies')} style={{ ...btnStyle, background: activeSubTab === 'casestudies' ? '#fff' : 'transparent', color: activeSubTab === 'casestudies' ? NAVY : '#64748b', boxShadow: activeSubTab === 'casestudies' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>Case Studies</button>
      </div>

      {loading ? <p style={{ fontFamily: FONT, color: '#64748b' }}>Loading content...</p> : activeSubTab === 'texttestimonials' ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontFamily: FONT, color: NAVY, margin: 0, fontSize: '18px' }}>Text Testimonials</h3>
            <button onClick={() => handleOpenModal('texttestimonial')} style={{ ...btnStyle, background: NAVY, color: '#fff' }}>+ Add Text Testimonial</button>
          </div>
          {textTestimonials.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', color: '#94a3b8', fontFamily: FONT }}>No text testimonials found.</div>
          ) : (
            <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
              {textTestimonials.map(t => (
                <div key={t.id} style={{ background: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundImage: `url(${t.image.startsWith('images/') ? '/' + t.image : t.image})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid #e2e8f0' }} />
                    <div>
                      <h4 style={{ margin: '0 0 2px', fontFamily: FONT, color: NAVY, fontSize: '16px' }}>{t.author}</h4>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{t.location}</span>
                    </div>
                  </div>
                  <p style={{ margin: '0 0 20px', fontFamily: FONT, fontSize: '14px', color: '#475569', lineHeight: 1.6, flex: 1 }}>"{t.text}"</p>
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => requestDelete('texttestimonials', t.id)} style={{ padding: '6px 12px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT, fontSize: '12px', fontWeight: 600 }}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : activeSubTab === 'testimonials' ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontFamily: FONT, color: NAVY, margin: 0, fontSize: '18px' }}>Video Testimonials</h3>
            <button onClick={() => handleOpenModal('testimonial')} style={{ ...btnStyle, background: NAVY, color: '#fff' }}>+ Add Testimonial</button>
          </div>
          {testimonials.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', color: '#94a3b8', fontFamily: FONT }}>No testimonials found.</div>
          ) : (
            <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
              {testimonials.map(t => {
                const name = t.clientName || t.title || 'Unnamed Client'
                const text = t.quote || t.description || ''
                // Extract YouTube ID for thumbnail
                let ytId = null
                if (t.link) {
                  const match = t.link.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/)
                  if (match && match[2].length === 11) ytId = match[2]
                }

                return (
                  <div key={t.id} style={{ background: '#fff', borderRadius: '16px', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 15px rgba(0,46,83,0.04)', border: '1px solid #e2e8f0', overflow: 'hidden', transition: 'transform 0.2s', ':hover': { transform: 'translateY(-2px)' } }}>
                    {/* Thumbnail Header */}
                    <div style={{ width: '100%', height: '180px', background: ytId ? `url(https://img.youtube.com/vi/${ytId}/hqdefault.jpg) center/cover` : '#f1f5f9', position: 'relative', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(11,35,71,0.8), transparent)' }} />
                      {!ytId && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontFamily: FONT, fontSize: '13px' }}>No Video Thumbnail</div>}
                      <div style={{ position: 'absolute', bottom: '16px', left: '20px', right: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px', fontFamily: FONT, color: '#fff', fontSize: '18px', fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{name}</h4>
                          {t.location && <span style={{ fontFamily: FONT, fontSize: '12px', color: 'rgba(255,255,255,0.9)', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: '4px' }}>{t.location}</span>}
                        </div>
                        <div style={{ background: '#fff', padding: '4px 8px', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                          <span style={{ color: '#fbbf24', fontSize: '14px', letterSpacing: '1px' }}>{'★'.repeat(Number(t.rating) || 5)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Content Body */}
                    <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {text && (
                        <div style={{ background: '#f8fafb', padding: '16px', borderRadius: '10px', marginBottom: '16px', borderLeft: `3px solid ${GOLD}` }}>
                          <p style={{ margin: 0, fontFamily: FONT, fontSize: '13px', color: '#475569', lineHeight: 1.6, fontStyle: 'italic' }}>"{text}"</p>
                        </div>
                      )}
                      <a href={t.link} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: FONT, fontSize: '13px', color: '#3b82f6', textDecoration: 'none', fontWeight: 600, background: '#eff6ff', padding: '8px 12px', borderRadius: '8px', width: 'fit-content' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                        Watch Video
                      </a>
                      
                      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={() => requestDelete('testimonials', t.id)} style={{ padding: '8px 16px', background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT, fontSize: '12px', fontWeight: 600, transition: 'all 0.2s', ':hover': { background: '#fef2f2' } }}>Delete Testimonial</button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontFamily: FONT, color: NAVY, margin: 0, fontSize: '18px' }}>Case Studies</h3>
            <button onClick={() => handleOpenModal('casestudy')} style={{ ...btnStyle, background: NAVY, color: '#fff' }}>+ Add Case Study</button>
          </div>
          {caseStudies.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', color: '#94a3b8', fontFamily: FONT }}>No case studies found.</div>
          ) : (
            <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
              {caseStudies.map(c => (
                <div key={c.id} style={{ background: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                  {c.coverImage ? (
                    <div style={{ width: '100%', height: '140px', backgroundImage: `url(${c.coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                  ) : (
                    <div style={{ width: '100%', height: '140px', background: '#e2e8f0' }} />
                  )}
                  <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <h4 style={{ margin: 0, fontFamily: FONT, color: NAVY, fontSize: '16px' }}>{c.title}</h4>
                    {c.result && (
                      <div style={{ display: 'inline-block', padding: '4px 8px', background: '#f0fdf4', color: '#16a34a', borderRadius: '4px', fontSize: '12px', fontWeight: 700, fontFamily: FONT, alignSelf: 'flex-start' }}>
                        {c.result}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      {c.challenge && <p style={{ margin: '0 0 6px', fontFamily: FONT, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}><strong>Challenge:</strong> {c.challenge}</p>}
                      {c.strategy && <p style={{ margin: 0, fontFamily: FONT, fontSize: '13px', color: '#475569', lineHeight: 1.5 }}><strong>Strategy:</strong> {c.strategy}</p>}
                    </div>
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', display: 'flex', justifyContent: 'flex-end', marginTop: 'auto' }}>
                      <button onClick={() => requestDelete('casestudies', c.id)} style={{ padding: '6px 12px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT, fontSize: '12px', fontWeight: 600 }}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modern Modal for Forms */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
          <div style={{ background: '#fff', padding: '32px', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', animation: 'fadeIn 0.2s ease-out' }}>
            <h3 style={{ margin: '0 0 24px', fontFamily: FONT, color: NAVY, fontSize: '20px' }}>
              {modalType === 'testimonial' ? 'Add Video Testimonial' : modalType === 'texttestimonial' ? 'Add Text Testimonial' : 'Add Case Study'}
            </h3>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {modalType === 'texttestimonial' ? (
                <>
                  <div>
                    <label style={labelStyle}>Avatar Image *</label>
                    <div style={{ marginTop: '6px', padding: '20px', border: '2px dashed #cbd5e1', borderRadius: '8px', textAlign: 'center', background: '#f8fafb', cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}>
                      {coverImage ? (
                        <img src={coverImage} alt="Avatar Preview" style={{ maxHeight: '80px', borderRadius: '50%' }} />
                      ) : (
                        <p style={{ margin: 0, fontFamily: FONT, fontSize: '13px', color: '#64748b' }}>Click to upload avatar image</p>
                      )}
                      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Author Name *</label>
                      <input autoFocus required type="text" placeholder="e.g. Daniel Carter" value={clientName} onChange={e => setClientName(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Location / Role (Optional)</label>
                      <input type="text" placeholder="e.g. Home Buyer, UAE" value={location} onChange={e => setLocation(e.target.value)} style={inputStyle} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Testimonial Text *</label>
                    <textarea required rows="4" placeholder="Their review..." value={quote} onChange={e => setQuote(e.target.value)} style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
                  </div>
                </>
              ) : modalType === 'testimonial' ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>Client Name *</label>
                      <input autoFocus required type="text" placeholder="e.g. John Doe" value={clientName} onChange={e => setClientName(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Location / Role (Optional)</label>
                      <input type="text" placeholder="e.g. Investor from UK" value={location} onChange={e => setLocation(e.target.value)} style={inputStyle} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>YouTube Link *</label>
                    <input required type="text" placeholder="https://youtube.com/watch?v=..." value={link} onChange={e => setLink(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Rating</label>
                    <select value={rating} onChange={e => setRating(e.target.value)} style={inputStyle}>
                      <option value="5">5 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="3">3 Stars</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Text Quote</label>
                    <textarea rows="4" placeholder="An impactful quote from the video..." value={quote} onChange={e => setQuote(e.target.value)} style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label style={labelStyle}>Cover Image *</label>
                    <div style={{ marginTop: '6px', padding: '20px', border: '2px dashed #cbd5e1', borderRadius: '8px', textAlign: 'center', background: '#f8fafb', cursor: 'pointer' }} onClick={() => fileInputRef.current.click()}>
                      {coverImage ? (
                        <img src={coverImage} alt="Cover Preview" style={{ maxHeight: '120px', objectFit: 'contain', borderRadius: '4px' }} />
                      ) : (
                        <p style={{ margin: 0, fontFamily: FONT, fontSize: '13px', color: '#64748b' }}>Click to upload cover image</p>
                      )}
                      <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageUpload} style={{ display: 'none' }} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle}>Case Study Title *</label>
                    <input autoFocus required type="text" placeholder="e.g. Record-Breaking Sale in Downtown" value={title} onChange={e => setTitle(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Key Result / Metrics</label>
                    <input type="text" placeholder="e.g. Sold in 14 days at 10% above asking" value={result} onChange={e => setResult(e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>The Challenge</label>
                    <textarea rows="3" placeholder="What was the initial problem?" value={challenge} onChange={e => setChallenge(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                  <div>
                    <label style={labelStyle}>The Strategy</label>
                    <textarea rows="3" placeholder="What did you do to solve it?" value={strategy} onChange={e => setStrategy(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                </>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ ...btnStyle, background: '#f1f5f9', color: '#475569' }} disabled={isSubmitting}>Cancel</button>
                <button type="submit" style={{ ...btnStyle, background: NAVY, color: '#fff' }} disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Data'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modern Delete Confirmation Modal */}
      {itemToDelete && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
          <div style={{ background: '#fff', padding: '32px', borderRadius: '16px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', animation: 'fadeIn 0.2s ease-out', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }}>
              ⚠️
            </div>
            <h3 style={{ margin: '0 0 8px', fontFamily: FONT, color: NAVY, fontSize: '18px' }}>Confirm Deletion</h3>
            <p style={{ margin: '0 0 24px', fontFamily: FONT, color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to permanently delete this {itemToDelete.type === 'testimonials' || itemToDelete.type === 'texttestimonials' ? 'testimonial' : 'case study'}? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button type="button" onClick={() => setItemToDelete(null)} style={{ ...btnStyle, background: '#f1f5f9', color: '#475569', flex: 1 }}>Cancel</button>
              <button type="button" onClick={confirmDelete} style={{ ...btnStyle, background: '#ef4444', color: '#fff', flex: 1, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)' }}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
